import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Search } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BLOG_POSTS } from "@/lib/data";

const CATEGORIES = ["All", "IELTS Tips", "PTE Tips", "Visa Updates", "Study Abroad News", "Scholarships", "University Admissions"];

export default function Blog() {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    document.title = "Blog | Globe Vista Overseas Education";
  }, []);

  const filtered = BLOG_POSTS.filter((p) => (cat === "All" || p.category === cat) && p.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="blog-page">
      <Navbar />
      <main className="pt-32 pb-24">
        <section className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">Insights & News</div>
            <h1 className="text-5xl sm:text-6xl font-light tracking-tighter">
              The Globe Vista <span className="font-serif italic text-gradient-royal">Journal.</span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Test-prep guides, visa updates, scholarships and student success stories — hand-crafted by our expert counsellors.
            </p>

            <div className="mt-8 relative max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles..."
                data-testid="blog-search"
                className="pl-11 rounded-full"
              />
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  cat === c ? "bg-[#0F3D8C] text-white dark:bg-[#2563EB]" : "bg-secondary hover:bg-secondary/70"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground" data-testid="blog-empty">No articles found.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <motion.article
                  key={p.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`blog-post-${p.slug}`}
                  className="group cursor-pointer rounded-3xl overflow-hidden bg-card border border-border hover:border-[#D4AF37]/60 hover:-translate-y-1 transition-all shadow-sm hover:shadow-2xl"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <Badge className="absolute top-4 left-4 bg-white/90 text-[#0F3D8C] hover:bg-white">{p.category}</Badge>
                  </div>
                  <div className="p-6">
                    <div className="text-xs text-muted-foreground flex gap-3">
                      <span>{p.date}</span><span>·</span><span>{p.read}</span>
                    </div>
                    <h3 className="mt-3 font-heading font-semibold text-xl leading-snug group-hover:text-[#0F3D8C] dark:group-hover:text-[#60A5FA] transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
                    <div className="mt-4 flex items-center gap-2 text-sm font-medium text-[#D4AF37]">
                      Read Article <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
      <FloatingActions />
    </div>
  );
}
